"use client"

import * as React from "react"
import { Check, ChevronsUpDown, Search, Plus, Trash2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export interface ComboboxOption {
  value: string
  label: string
  icon?: React.ReactNode
  description?: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  onDeleteOption?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  allowCustomValue?: boolean
  deleteConfirmTitle?: string
  deleteConfirmDescription?: string
}

export function Combobox({
  options,
  value,
  onValueChange,
  onDeleteOption,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  emptyText = "No option found.",
  disabled = false,
  className,
  allowCustomValue = false,
  deleteConfirmTitle = "Delete this option?",
  deleteConfirmDescription = "This action cannot be undone.",
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [optionToDelete, setOptionToDelete] = React.useState<string | null>(null)

  const handleDeleteClick = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation()
    setOptionToDelete(optionValue)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (optionToDelete && onDeleteOption) {
      onDeleteOption(optionToDelete)
    }
    setDeleteDialogOpen(false)
    setOptionToDelete(null)
  }

  // For custom values, also check if value matches search
  const selectedOption = options.find((option) => option.value === value)
  const displayValue = selectedOption?.label || (allowCustomValue && value ? value : null)

  const filteredOptions = React.useMemo(() => {
    if (!search) return options
    
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase()) ||
      option.value.toLowerCase().includes(search.toLowerCase()) ||
      option.description?.toLowerCase().includes(search.toLowerCase())
    )
  }, [options, search])

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          {displayValue ? (
            <div className="flex items-center gap-2 flex-1 text-left">
              {selectedOption?.icon}
              <div className="flex flex-col">
                <span>{displayValue}</span>
                {selectedOption?.description && (
                  <span className="text-xs text-muted-foreground">
                    {selectedOption.description}
                  </span>
                )}
              </div>
            </div>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-full max-w-none" align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
        <div className="border-b px-3 py-2">
          <div className="flex items-center">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
            />
          </div>
        </div>
        <div 
          className="p-1"
          style={{
            height: '200px',
            overflowY: 'scroll',
            overflowX: 'hidden'
          }}
        >
          {filteredOptions.length === 0 && !allowCustomValue ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {emptyText}
            </div>
          ) : (
            <>
              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground group"
                  onClick={() => {
                    const newValue = option.value === value ? "" : option.value
                    onValueChange?.(newValue)
                    setOpen(false)
                    setSearch("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
                    {option.icon}
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-left">{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground truncate text-left">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                  {onDeleteOption && (
                    <button
                      type="button"
                      className="ml-2 p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={(e) => handleDeleteClick(e, option.value)}
                      title="Delete this option"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {/* Show option to add custom value */}
              {allowCustomValue && (
                <>
                  {/* Show "Add [typed value]" when user types something new */}
                  {search && !options.some(o => o.value.toLowerCase() === search.toLowerCase()) && (
                    <div
                      className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground border-t mt-1 pt-2"
                      onClick={() => {
                        onValueChange?.(search)
                        setOpen(false)
                        setSearch("")
                      }}
                    >
                      <Plus className="mr-2 h-4 w-4 text-primary" />
                      <span className="text-muted-foreground">Add "</span>
                      <span className="font-medium">{search}</span>
                      <span className="text-muted-foreground">"</span>
                    </div>
                  )}
                  {/* Show hint when no search and options exist */}
                  {!search && options.length > 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground border-t mt-1 pt-2">
                      Type to create a new tag
                    </div>
                  )}
                  {/* Show hint when no options exist */}
                  {!search && options.length === 0 && (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      Type to create a new tag
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>

    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{deleteConfirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>
            {deleteConfirmDescription.replace('{option}', optionToDelete || '')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}